import { useRef } from 'react';

type DiscountBadgeSvgProps = {
	variant: 'fixed' | 'percentage';
	fixedValue?: string;
	percentageValue?: number;
	title?: string;
	className?: string;
};

let badgeIdCounter = 0;

const getNextBadgeId = () => {
	badgeIdCounter += 1;
	return `discount-badge-${badgeIdCounter}`;
};

const getFixedFontSize = (value: string) => {
	if (value.length <= 8) {
		return 54;
	}

	if (value.length <= 11) {
		return 46;
	}

	if (value.length <= 14) {
		return 40;
	}

	return 34;
};

const getPercentageNumberFontSize = (digits: number) => {
	if (digits <= 1) return 82;
	if (digits <= 2) return 72;
	return 62;
};

const getPercentageLayout = (digits: number) => {
	if (digits <= 1) {
		return { valueY: 100, offY: 168, offFontSize: 48 };
	}

	if (digits <= 2) {
		return { valueY: 100, offY: 170, offFontSize: 48 };
	}

	return { valueY: 102, offY: 172, offFontSize: 44 };
};

export const DiscountBadgeSvg = ({
	variant,
	fixedValue,
	percentageValue,
	title,
	className,
}: DiscountBadgeSvgProps) => {
	const baseIdRef = useRef<string>();

	if (!baseIdRef.current) {
		baseIdRef.current = getNextBadgeId();
	}

	const uniqueId = baseIdRef.current;
	const frameId = `${uniqueId}-badge-frame`;
	const accentsId = `${uniqueId}-badge-accents`;
	const shellMaskId = `${uniqueId}-badge-shell-mask`;
	const safeFixedValue = fixedValue?.trim() || '--';
	const safePercentageValue = Number.isFinite(percentageValue)
		? Math.max(0, Math.round(Number(percentageValue)))
		: 0;
	const percentageLabel = `${safePercentageValue}%`;
	const percentageNumber = String(safePercentageValue);
	const percentageLayout = getPercentageLayout(percentageNumber.length);
	const primaryLabel = variant === 'fixed' ? safeFixedValue : percentageLabel;
	const ariaLabel = variant === 'fixed'
		? `${title ? `${title}. ` : ''}${safeFixedValue} OFF de desconto`
		: `${title ? `${title}. ` : ''}${percentageLabel} de desconto`;

	return (
		<svg
			className={className}
			viewBox='0 0 360 288'
			role='img'
			aria-label={ariaLabel}
			focusable='false'
			preserveAspectRatio='xMidYMid meet'
		>
			<defs>
				<mask id={shellMaskId} maskUnits='userSpaceOnUse'>
					<rect x='34' y='28' width='292' height='204' rx='28' fill='white' />
					<circle cx='34' cy='181' r='16' fill='black' />
					<circle cx='326' cy='181' r='16' fill='black' />
				</mask>
				<symbol id={frameId} viewBox='0 0 360 288'>
					<rect
						x='34'
						y='28'
						width='292'
						height='204'
						rx='28'
						fill='var(--badge-cream, #FFFBE9)'
						mask={`url(#${shellMaskId})`}
					/>
					<rect
						x='34'
						y='28'
						width='292'
						height='204'
						rx='28'
						fill='none'
						stroke='var(--badge-red, #E73F5D)'
						strokeWidth='12'
						mask={`url(#${shellMaskId})`}
					/>
					<rect
						x='34'
						y='188'
						width='292'
						height='44'
						rx='8'
						fill='var(--badge-red, #E73F5D)'
						mask={`url(#${shellMaskId})`}
					/>
				</symbol>
				<symbol id={accentsId} viewBox='0 0 360 288'>
					<path d='M39 86l28-10 8 24-28 10z' fill='var(--badge-red, #E73F5D)' />
					<path d='M321 76l-28-10-8 24 28 10z' fill='var(--badge-red, #E73F5D)' />
					<circle cx='52' cy='181' r='4.5' fill='var(--badge-red, #E73F5D)' />
					<circle cx='308' cy='181' r='4.5' fill='var(--badge-red, #E73F5D)' />
				</symbol>
			</defs>

			<g aria-hidden='true'>
				<use href={`#${frameId}`} />
				<use href={`#${accentsId}`} />

				<line
					x1='88'
					y1='20'
					x2='272'
					y2='12'
					stroke='var(--badge-red, #E73F5D)'
					strokeWidth='3'
					strokeLinecap='round'
					opacity='0.35'
				/>
				<line
					x1='84'
					y1='250'
					x2='276'
					y2='258'
					stroke='var(--badge-red, #E73F5D)'
					strokeWidth='3'
					strokeLinecap='round'
					opacity='0.35'
				/>

				<text
					x='180'
					y='216'
					textAnchor='middle'
					fill='var(--badge-cream, #FFFBE9)'
					fontSize='15'
					fontWeight='800'
					letterSpacing='1.2'
				>
					OFERTA RELÂMPAGO
				</text>
			</g>

			<g transform='rotate(-7 180 114)'>
				{variant === 'fixed' ? (
					<text
						x='180'
						y='118'
						textAnchor='middle'
						fill='var(--badge-red, #E73F5D)'
						fontWeight='900'
						fontSize={getFixedFontSize(primaryLabel)}
					>
						<tspan x='180' dominantBaseline='middle'>{primaryLabel}</tspan>
						<tspan x='180' dy='48' fontSize='36' letterSpacing='2'>OFF</tspan>
					</text>
				) : (
					<>
						<text
							x='180'
							y={percentageLayout.valueY}
							textAnchor='middle'
							fill='var(--badge-red, #E73F5D)'
							fontWeight='900'
							fontSize={getPercentageNumberFontSize(percentageNumber.length)}
							dominantBaseline='middle'
						>
							{percentageNumber}%
						</text>
						<text
							x='180'
							y={percentageLayout.offY}
							textAnchor='middle'
							fill='var(--badge-red, #E73F5D)'
							fontWeight='900'
							fontSize={percentageLayout.offFontSize}
							letterSpacing='1.6'
						>
							OFF
						</text>
					</>
				)}
			</g>
		</svg>
	);
};
